/**
 * "Fill from" on a custom safety form field: where the fill screen takes the
 * field's starting value from.
 *
 * Stored as `options.prefill`, NOT `options.maps_to`. `maps_to` is the
 * approval-stamp namespace (`ticket.po_number`, …) read by the API's
 * approval-stamp resolver, and the form-extraction validator strips any
 * `maps_to` outside its allow-list. A prefill key there would either be
 * stripped on an extraction approve or be read as a stamp binding. The
 * template routes store `options` as given, so a new sibling key round-trips.
 *
 * The rule the fill screen keeps: a prefill never overwrites what a person
 * typed. A field is written only while nobody has touched it, and only when it
 * is empty or still holds the value this module put there. Unlinking the job
 * clears only the values the job filled.
 */
import { getUserDisplayName } from './user-display';

export const PREFILL_SOURCES = [
  'job_rig',
  'job_customer',
  'job_location',
  'job_supervisor',
  'current_user',
  'today',
] as const;
export type PrefillSource = (typeof PREFILL_SOURCES)[number];

export const PREFILL_LABELS: Record<PrefillSource, string> = {
  job_rig: 'Job rig',
  job_customer: 'Job customer',
  job_location: 'Job location',
  job_supervisor: 'Job supervisor / crew lead',
  current_user: 'Current user',
  today: 'Today',
};

/**
 * Which sources make sense on which field type. A date takes today and nothing
 * else; a person picker takes a person; a text or dropdown takes any of the
 * text values. Every other type offers no "Fill from" at all.
 */
export function prefillSourcesFor(fieldType: string): PrefillSource[] {
  switch (fieldType) {
    case 'text':
    case 'textarea':
    case 'dropdown':
      return ['job_rig', 'job_customer', 'job_location', 'job_supervisor', 'current_user'];
    case 'worker_select':
      return ['job_supervisor', 'current_user'];
    case 'date':
      return ['today'];
    default:
      return [];
  }
}

/** The field's prefill source, or null when it has none or one its type cannot take. */
export function fieldPrefill(field: { field_type: string; options?: any }): PrefillSource | null {
  const raw = field.options?.prefill;
  if (typeof raw !== 'string') return null;
  const src = raw as PrefillSource;
  return prefillSourcesFor(field.field_type).includes(src) ? src : null;
}

/** The slice of a linked job the prefill reads (GET /safety/form-jobs row). */
export interface PrefillJob {
  customer_name: string | null;
  well_name: string | null;
  well_id_lsd: string | null;
  location_city: string | null;
  location_province_state: string | null;
  rig_name: string | null;
}

export interface PrefillPerson {
  id: string;
  name: string;
}

export interface PrefillContext {
  job: PrefillJob | null;
  /** The job's supervisor or crew lead, when one can be named (see jobSupervisor). */
  supervisor: PrefillPerson | null;
  user: PrefillPerson | null;
  /** 'YYYY-MM-DD' in the tenant's zone, or null while the zone is unknown. */
  today: string | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Well and LSD, else city and province — the same "where" the job picker shows. */
function jobLocation(job: PrefillJob): string | null {
  const well = str(job.well_name);
  const lsd = str(job.well_id_lsd);
  if (well || lsd) return [well, lsd].filter(Boolean).join(' · ');
  const city = str(job.location_city);
  const prov = str(job.location_province_state);
  return city ? [city, prov].filter(Boolean).join(', ') : null;
}

/**
 * The job's supervisor or crew lead from GET /jobs/:id `crew`.
 *
 * Jobs carry no supervisor column. What the crew rows do carry is
 * `role_on_job` (free-text display copy) and `is_ticket_writer` (the one
 * designation the API enforces, at most one per job). So: the one crew member
 * whose role on the job says "supervisor", else the designated ticket writer,
 * else nobody. Two "supervisors" is not one, and guessing between them would
 * put a name on a safety record that the job does not support — the field
 * stays blank instead.
 */
export function jobSupervisor(crew: unknown): PrefillPerson | null {
  if (!Array.isArray(crew)) return null;
  const person = (m: any): PrefillPerson | null =>
    m && typeof m.id === 'string' ? { id: m.id, name: getUserDisplayName(m, '') } : null;
  const supervisors = crew.filter((m: any) => /supervisor/i.test(String(m?.role_on_job ?? '')));
  if (supervisors.length === 1) return person(supervisors[0]);
  if (supervisors.length > 1) return null;
  const writer = crew.find((m: any) => m?.is_ticket_writer === true);
  return writer ? person(writer) : null;
}

/** What `source` puts in a field of `fieldType`, or null when the context has nothing for it. */
export function resolvePrefill(source: PrefillSource, fieldType: string, ctx: PrefillContext): unknown {
  const personValue = (p: PrefillPerson | null) => {
    if (!p) return null;
    if (fieldType === 'worker_select') return [p.id];
    return p.name || null;
  };
  switch (source) {
    case 'job_rig': return ctx.job ? str(ctx.job.rig_name) : null;
    case 'job_customer': return ctx.job ? str(ctx.job.customer_name) : null;
    case 'job_location': return ctx.job ? jobLocation(ctx.job) : null;
    case 'job_supervisor': return ctx.job ? personValue(ctx.supervisor) : null;
    case 'current_user': return personValue(ctx.user);
    case 'today': return ctx.today;
  }
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * One prefill pass. Pure: returns the next answers and the next record of
 * what the prefill itself wrote.
 *
 *   touched  fields a person has edited — never written again, even if cleared
 *   filled   field id → the value this function last wrote there
 *
 * A field is written when it is untouched AND either empty or still holding
 * exactly what we filled. When its source has no value (the job was removed,
 * or the new job has no rig), a value WE filled is removed and anything else
 * is left alone.
 */
export function applyPrefill(
  fields: Array<{ id: string; field_type: string; options?: any }>,
  answers: Record<string, any>,
  filled: Record<string, unknown>,
  touched: ReadonlySet<string>,
  ctx: PrefillContext,
): { answers: Record<string, any>; filled: Record<string, unknown> } {
  let nextAnswers = answers;
  const nextFilled = { ...filled };
  const write = (id: string, v: unknown) => {
    if (nextAnswers === answers) nextAnswers = { ...answers };
    if (v === undefined) delete nextAnswers[id];
    else nextAnswers[id] = v;
  };
  for (const f of fields) {
    const source = fieldPrefill(f);
    if (!source || touched.has(f.id)) continue;
    const current = answers[f.id];
    const ours = f.id in nextFilled && same(current, nextFilled[f.id]);
    if (!isEmpty(current) && !ours) continue; // someone else's value — a draft's, say
    const value = resolvePrefill(source, f.field_type, ctx);
    if (isEmpty(value)) {
      if (ours) write(f.id, undefined);
      delete nextFilled[f.id];
      continue;
    }
    if (!same(current, value)) write(f.id, value);
    nextFilled[f.id] = value;
  }
  return { answers: nextAnswers, filled: nextFilled };
}
